import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { removePosition, setBalance, removePendingOrder, updatePosition } from '../../store/tradeSlice';
import { showToast } from '../../store/uiSlice';
import { fmtP, fmtUSD } from '../../utils/formatters';
import { getLotSize } from '../../utils/constants';
import { calcForexPnl } from '../../utils/forexUtils';
import api from '../../services/api';

const calcPnl = (isShort, currentPrice, avgPrice, qty, ls, instrumentId, cat) =>
  calcForexPnl(isShort, currentPrice, avgPrice, qty, instrumentId, cat, ls);

const fmtSL = (val) => (Number(val) > 0 ? Number(val).toFixed(2) : '0.00');
const fmtTP = (val) => (Number(val) > 0 ? Number(val).toFixed(2) : '0.00');

const fmtDT = (dt) => {
  if (!dt) return '—';
  const d = new Date(dt);
  if (isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
};

/**
 * ✅ SINGLE SOURCE OF TRUTH for active-position P&L — identical to BottomPanel.
 */
const getPositionNetPnl = (pos, marketData) => {
  const rawQty       = Number(pos.qty) || 0;
  const qty          = Math.abs(rawQty);
  const avgPrice     = Number(pos.avgPrice) || 0;
  const cat          = pos.category || 'crypto';
  const isShort      = pos.direction === 'sell' || rawQty < 0;
  const currentPrice = Number(marketData[pos.instrumentId]?.price) || avgPrice;
  const ls           = getLotSize(cat, pos.instrumentId);
  const pnl          = calcPnl(isShort, currentPrice, avgPrice, qty, ls, pos.instrumentId, cat);
  const swap         = Number(pos.swap) || 0;
  const commission   = Number(pos.commission) || 0;
  return pnl - swap - commission;
};

/**
 * ✅ SINGLE SOURCE OF TRUTH for history-order P&L — identical to BottomPanel.
 * Backend's `order.pnl` already has commission deducted, so only swap is subtracted.
 */
const getHistoryNetPnl = (order) => {
  const pnl  = Number(order.pnl) || 0;
  const swap = Number(order.swap) || 0;
  return pnl - swap;
};

const Badge = ({ children, color, isLight }) => {
  const map = {
    long: isLight
      ? 'bg-[#059669] text-white border-[#059669]'
      : 'bg-[rgba(16,185,129,0.18)] text-[#34d399] border-[rgba(16,185,129,0.3)]',
    short: isLight
      ? 'bg-[#dc2626] text-white border-[#dc2626]'
      : 'bg-[rgba(239,68,68,0.18)] text-[#f87171] border-[rgba(239,68,68,0.3)]',
    orange: 'bg-[rgba(46,54,66,0.5)] text-[#94a3b8] border-[rgba(46,54,66,0.6)]',
    purple: 'bg-[rgba(139,92,246,0.18)] text-[#a78bfa] border-[rgba(139,92,246,0.3)]',
    teal: 'bg-[rgba(20,184,166,0.18)] text-[#2dd4bf] border-[rgba(20,184,166,0.3)]',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold font-mono border whitespace-nowrap ${map[color]}`}>
      {children}
    </span>
  );
};

const getDirectionLabel = (trade) => {
  const isLong = trade.direction === 'buy';
  return { label: isLong ? 'LONG' : 'SHORT', color: isLong ? 'long' : 'short' };
};

const FullViewPanel = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { portfolio, pendingOrders, historyOrders, marketData, balance } = useSelector((s) => s.trade);
  const theme = useSelector((s) => s.ui.theme);
  const isLight = theme === 'light';
  const [activeTab, setActiveTab] = useState('active');
  const [isLoading, setIsLoading] = useState(false);

  const [editingSlTp, setEditingSlTp] = useState(null);
  const [editSl, setEditSl] = useState('');
  const [editTp, setEditTp] = useState('');
  const [savingSlTp, setSavingSlTp] = useState(false);

  const [showPartialClose, setShowPartialClose] = useState(null);
  const [partialCloseQty, setPartialCloseQty] = useState('');
  const [partialClosing, setPartialClosing] = useState(false);

  // ✅ Same credit/bonus fetch as BottomPanel — keeps Equity/Free Margin identical
  const [totalCredit, setTotalCredit] = useState(0);
  const [bonusBalance, setBonusBalance] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    const fetchCreditAndBonus = async () => {
      try {
        const [financeRes, creditRes] = await Promise.all([
          api.get('/user/finance/summary'),
          api.get('/user/credit/requests'),
        ]);
        setBonusBalance(financeRes.data?.bonusBalance || 0);
        if (creditRes.data && Array.isArray(creditRes.data)) {
          const approved = creditRes.data
            .filter(r => r.status === 'approved')
            .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
          setTotalCredit(approved);
        }
      } catch (e) {}
    };
    fetchCreditAndBonus();
  }, [isOpen]);

  if (!isOpen) return null;

  const tabs = [
    { key: 'active',  label: 'Active',  icon: 'fa-circle-check',      count: Object.keys(portfolio).length },
    { key: 'pending', label: 'Pending', icon: 'fa-clock',             count: pendingOrders.length },
    { key: 'history', label: 'History', icon: 'fa-clock-rotate-left', count: historyOrders.length },
  ];

  // ✅ Active P&L identical to BottomPanel: pnl - swap - commission
  const totalLivePnl = Object.values(portfolio).reduce(
    (acc, pos) => acc + getPositionNetPnl(pos, marketData),
    0
  );

  // ✅ History P&L identical to BottomPanel: pnl - swap
  const totalHistoryPnl = historyOrders.reduce(
    (acc, o) => acc + getHistoryNetPnl(o),
    0
  );

  const totalMarginUsed = Object.values(portfolio).reduce((s, p) => s + (Number(p.cost) || 0), 0);
  const currentBalance  = Number(balance) || 0;
  const equity          = currentBalance + totalLivePnl + totalCredit + bonusBalance;
  const freeMargin      = equity - totalMarginUsed;
  const marginLevel     = totalMarginUsed > 0 ? (equity / totalMarginUsed) * 100 : 0;

  const handleClosePosition = async (positionId) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const position = portfolio[positionId];
      const currentPrice = marketData[position.instrumentId]?.price;
      if (!currentPrice) { alert('Price not available'); return; }
      const response = await api.post('/trade/close', {
        instrumentId: position.instrumentId,
        positionId: positionId,
        closePrice: currentPrice
      });
      if (response.data.success) {
        dispatch(setBalance(response.data.newBalance));
        dispatch(removePosition(positionId));
        dispatch(showToast({ message: 'Position closed successfully', type: 'success' }));
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to close position');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await api.delete(`/orders/${orderId}`);
      if (response.data.success) {
        dispatch(removePendingOrder(orderId));
        dispatch(showToast({ message: 'Order cancelled', type: 'success' }));
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to cancel order');
    } finally {
      setIsLoading(false);
    }
  };

  const startEditSlTp = (positionId, currentSl, currentTp) => {
    setEditingSlTp(positionId);
    setEditSl(Number(currentSl) > 0 ? String(Number(currentSl)) : '');
    setEditTp(Number(currentTp) > 0 ? String(Number(currentTp)) : '');
  };

  const cancelEditSlTp = () => {
    setEditingSlTp(null);
    setEditSl('');
    setEditTp('');
  };

  const saveSlTp = async (positionId) => {
    if (savingSlTp) return;
    setSavingSlTp(true);
    try {
      const sl = editSl ? Number(editSl) : 0;
      const tp = editTp ? Number(editTp) : 0;
      const response = await api.put(`/trade/update-sl-tp/${positionId}`, { sl, tp });
      if (response.data?.position) {
        dispatch(updatePosition({ instrumentId: response.data.position.instrumentId, position: response.data.position }));
        dispatch(showToast({ message: 'SL/TP updated', type: 'success' }));
      }
      cancelEditSlTp();
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || 'Failed to update SL/TP', type: 'error' }));
    } finally {
      setSavingSlTp(false);
    }
  };

  const openPartialClose = (positionId) => {
    setShowPartialClose(positionId);
    setPartialCloseQty('');
  };

  const closePartialCloseModal = () => {
    setShowPartialClose(null);
    setPartialCloseQty('');
  };

  const handlePartialClose = async () => {
    if (partialClosing || !showPartialClose) return;
    const qty = Number(partialCloseQty);
    const pos = portfolio[showPartialClose];
    const totalQty = Math.abs(Number(pos?.qty)) || 0;

    if (!qty || qty <= 0 || qty >= totalQty) {
      dispatch(showToast({ message: `Enter lots between 0.01 and ${(totalQty - 0.01).toFixed(2)}`, type: 'error' }));
      return;
    }

    setPartialClosing(true);
    try {
      const response = await api.post(`/trade/partial-close/${showPartialClose}`, { closeQuantity: qty });
      if (response.data.success) {
        dispatch(setBalance(response.data.newBalance));
        if (response.data.position) {
          dispatch(updatePosition({ instrumentId: response.data.instrumentId, position: response.data.position }));
        } else {
          dispatch(removePosition(showPartialClose));
        }
        dispatch(showToast({
          message: `Closed ${qty.toFixed(2)} lots · P&L: ${response.data.pnl >= 0 ? '+' : ''}$${response.data.pnl.toFixed(2)}`,
          type: 'success'
        }));
      }
      closePartialCloseModal();
    } catch (err) {
      dispatch(showToast({ message: err.response?.data?.message || 'Partial close failed', type: 'error' }));
    } finally {
      setPartialClosing(false);
    }
  };

  const renderActive = () => {
    const positions = Object.entries(portfolio);
    if (positions.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-5" style={{ background: 'var(--color-bgCard)', border: '1px solid var(--color-borderMedium)' }}>
            <i className="fas fa-inbox text-3xl" style={{ color: 'var(--color-textDim)' }} />
          </div>
          <p className="font-mono text-base font-bold" style={{ color: 'var(--color-textSecondary)' }}>No active positions</p>
          <p className="font-mono text-sm mt-1" style={{ color: 'var(--color-textSecondary)' }}>Place a trade to get started</p>
        </div>
      );
    }

    const thStyle = { color: 'var(--color-textSecondary)', fontSize: '12px', fontFamily: 'monospace', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' };
    const tdText = { color: 'var(--color-text)', fontSize: '14px', fontFamily: 'monospace', fontWeight: 700 };
    const tdMuted = { color: 'var(--color-textSecondary)', fontSize: '14px', fontFamily: 'monospace', fontWeight: 700 };

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b-2" style={{ borderColor: 'var(--color-borderMedium)' }}>
              <th className="px-4 py-3 text-left" style={thStyle}>ID</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Instrument</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Type</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Amount</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Trade Vol</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Open Rate</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Open Time</th>
              <th className="px-4 py-3 text-left" style={thStyle}>S/L</th>
              <th className="px-4 py-3 text-left" style={thStyle}>T/P</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Swap</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Total Profit</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Margin</th>
              <th className="px-4 py-3 text-right" style={thStyle}>Action</th>
            </tr>
          </thead>
          <tbody>
            {[...positions].sort(([, a], [, b]) => {
              const dateA = new Date(a.openTime || a.createdAt || 0);
              const dateB = new Date(b.openTime || b.createdAt || 0);
              return dateB - dateA;
            }).map(([id, pos]) => {
              const rawQty = Number(pos.qty) || 0;
              const qty = Math.abs(rawQty);
              const avgPrice = Number(pos.avgPrice) || 0;
              const cat = pos.category || 'crypto';
              const isShort = pos.direction === 'sell' || rawQty < 0;
              const ls = getLotSize(cat, pos.instrumentId);
              const tradeVolume = (qty * ls).toLocaleString('en-US', { maximumFractionDigits: 2 });
              const swap = Number(pos.swap) || 0;
              const margin = Number(pos.cost) || 0;
              // ✅ Same function used by footer — row and total will always match
              const totalProfit = getPositionNetPnl(pos, marketData);
              const posId = pos.id ? String(pos.id).slice(-6) : id.slice(0, 6);
              const isEditing = editingSlTp === pos.id;

              return (
                <tr key={id} className="border-b transition-all" style={{ borderColor: 'var(--color-borderLight)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bgHover)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td className="px-4 py-3.5" style={tdMuted}>{posId}</td>
                  <td className="px-4 py-3.5 font-syne font-extrabold text-base flex items-center gap-2" style={tdText}>
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isShort ? 'bg-[#ef4444]' : 'bg-[#10b981]'}`} />
                    {pos.instrumentId}
                  </td>
                  <td className="px-4 py-3.5"><Badge color={isShort ? 'short' : 'long'} isLight={isLight}>{isShort ? 'SHORT' : 'LONG'}</Badge></td>
                  <td className="px-4 py-3.5" style={tdText}>{qty.toFixed(2)}</td>
                  <td className="px-4 py-3.5" style={tdText}>{tradeVolume}</td>
                  <td className="px-4 py-3.5" style={tdText}>{fmtP(avgPrice, cat)}</td>
                  <td className="px-4 py-3.5 text-xs" style={tdMuted}>{fmtDT(pos.openTime || pos.createdAt)}</td>

                  <td className="px-4 py-3.5" style={{ ...tdText, color: Number(pos.sl) > 0 ? 'var(--color-redLight)' : 'var(--color-textDim)' }}>
                    {isEditing ? (
                      <input type="number" value={editSl} onChange={e => setEditSl(e.target.value)} step="any"
                        className="w-20 rounded px-2 py-1 text-sm font-bold outline-none"
                        style={{ background: 'var(--color-bgInput)', border: '1px solid var(--color-borderMedium)', color: 'var(--color-redLight)' }} placeholder="SL" />
                    ) : fmtSL(pos.sl)}
                  </td>

                  <td className="px-4 py-3.5" style={{ ...tdText, color: Number(pos.tp) > 0 ? 'var(--color-greenLight)' : 'var(--color-textDim)' }}>
                    {isEditing ? (
                      <input type="number" value={editTp} onChange={e => setEditTp(e.target.value)} step="any"
                        className="w-20 rounded px-2 py-1 text-sm font-bold outline-none"
                        style={{ background: 'var(--color-bgInput)', border: '1px solid var(--color-borderMedium)', color: 'var(--color-greenLight)' }} placeholder="TP" />
                    ) : fmtTP(pos.tp)}
                  </td>

                  <td className="px-4 py-3.5" style={{ ...tdText, color: swap !== 0 ? 'var(--color-orangeLight)' : 'var(--color-textDim)' }}>
                    {swap !== 0 ? (swap < 0 ? '-$' : '$') + Math.abs(swap).toFixed(2) : '$0.00'}
                  </td>
                  <td className="px-4 py-3.5 font-mono text-base font-extrabold" style={{ color: totalProfit >= 0 ? 'var(--color-green)' : 'var(--color-red)' }}>
                    {totalProfit >= 0 ? '+$' : '-$'}{Math.abs(totalProfit).toFixed(2)}
                  </td>
                  <td className="px-4 py-3.5" style={tdText}>{margin > 0 ? '$' + margin.toFixed(2) : '—'}</td>
                  <td className="px-4 py-3.5 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {isEditing ? (
                        <>
                          <button onClick={() => saveSlTp(pos.id)} disabled={savingSlTp}
                            className="px-3 py-1.5 rounded-lg text-[10px] font-syne font-bold transition-all disabled:opacity-40"
                            style={{ background: 'var(--color-greenBg)', border: '1px solid var(--color-greenBorder)', color: 'var(--color-greenLight)' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(16,185,129,0.22)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'var(--color-greenBg)'}>
                            {savingSlTp ? '...' : '✓ Save'}
                          </button>
                          <button onClick={cancelEditSlTp}
                            className="px-3 py-1.5 rounded-lg text-[10px] font-syne font-bold transition-all"
                            style={{ background: 'var(--color-redBg)', border: '1px solid var(--color-redBorder)', color: 'var(--color-redLight)' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.18)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'var(--color-redBg)'}>
                            ✕ Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEditSlTp(pos.id, pos.sl, pos.tp)}
                            className="px-3 py-1.5 rounded-lg text-[10px] font-syne font-bold transition-all"
                            style={{ background: 'var(--color-purpleBg)', border: '1px solid var(--color-purpleBorder)', color: 'var(--color-purpleLight)' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(139,92,246,0.18)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'var(--color-purpleBg)'}>
                            <i className="fas fa-pen mr-1 text-[9px]" />SL/TP
                          </button>
                          <button onClick={() => openPartialClose(id)}
                            className="px-3 py-1.5 rounded-lg text-[10px] font-syne font-bold transition-all"
                            style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)', color: 'var(--color-text)' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(46,54,66,0.55)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'var(--color-orangeBg)'}>
                            <i className="fas fa-scissors mr-1 text-[9px]" />Part
                          </button>
                          <button onClick={() => handleClosePosition(id)} disabled={isLoading}
                            className="px-3 py-1.5 rounded-lg text-[10px] font-syne font-bold transition-all disabled:opacity-40"
                            style={{ background: 'var(--color-redBg)', border: '1px solid var(--color-redBorder)', color: 'var(--color-redLight)' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.2)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'var(--color-redBg)'}>
                            <i className="fas fa-xmark mr-1 text-[9px]" />Close
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPending = () => {
    if (pendingOrders.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-5" style={{ background: 'var(--color-bgCard)', border: '1px solid var(--color-borderMedium)' }}>
            <i className="fas fa-clock text-3xl" style={{ color: 'var(--color-textDim)' }} />
          </div>
          <p className="font-mono text-base font-bold" style={{ color: 'var(--color-textSecondary)' }}>No pending orders</p>
          <p className="font-mono text-sm mt-1" style={{ color: 'var(--color-textSecondary)' }}>Place a limit or stop order</p>
        </div>
      );
    }

    const thStyle = { color: 'var(--color-textSecondary)', fontSize: '12px', fontFamily: 'monospace', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' };
    const tdText = { color: 'var(--color-text)', fontSize: '14px', fontFamily: 'monospace', fontWeight: 700 };

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b-2" style={{ borderColor: 'var(--color-borderMedium)' }}>
              <th className="px-4 py-3 text-left" style={thStyle}>ID</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Symbol</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Type</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Lots</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Trade Vol</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Target</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Current</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Distance</th>
              <th className="px-4 py-3 text-left" style={thStyle}>S/L</th>
              <th className="px-4 py-3 text-left" style={thStyle}>T/P</th>
              <th className="px-4 py-3 text-right" style={thStyle}>Action</th>
            </tr>
          </thead>
          <tbody>
            {pendingOrders.filter(Boolean).map(order => {
              const qty = Number(order.qty) || 0;
              const targetPrice = Number(order.targetPrice) || 0;
              const currentPrice = Number(marketData[order.instId]?.price) || 0;
              const dist = currentPrice ? ((targetPrice - currentPrice) / currentPrice * 100).toFixed(2) : '—';
              const isBuy = order.direction === 'buy';
              const cat = order.cat || 'crypto';
              const tradeVolume = (qty * getLotSize(cat, order.instId)).toLocaleString();
              const ordId = order.id ? String(order.id).slice(-6) : '—';

              return (
                <tr key={order.id} className="border-b transition-all" style={{ borderColor: 'var(--color-borderLight)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bgHover)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td className="px-4 py-3.5" style={{ ...tdText, color: 'var(--color-textSecondary)' }}>{ordId}</td>
                  <td className="px-4 py-3.5 font-syne font-extrabold text-base" style={tdText}>{order.instId}</td>
                  <td className="px-4 py-3.5"><Badge color={isBuy ? 'long' : 'short'} isLight={isLight}>{isBuy ? 'LONG' : 'SHORT'} {order.type?.toUpperCase()}</Badge></td>
                  <td className="px-4 py-3.5" style={tdText}>{qty.toFixed(2)}</td>
                  <td className="px-4 py-3.5" style={tdText}>{tradeVolume}</td>
                  <td className="px-4 py-3.5" style={tdText}>{fmtP(targetPrice, cat)}</td>
                  <td className="px-4 py-3.5" style={tdText}>{fmtP(currentPrice, cat)}</td>
                  <td className="px-4 py-3.5" style={tdText}>{dist}%</td>
                  <td className="px-4 py-3.5" style={{ ...tdText, color: Number(order.sl) > 0 ? 'var(--color-redLight)' : 'var(--color-textDim)' }}>{fmtSL(order.sl)}</td>
                  <td className="px-4 py-3.5" style={{ ...tdText, color: Number(order.tp) > 0 ? 'var(--color-greenLight)' : 'var(--color-textDim)' }}>{fmtTP(order.tp)}</td>
                  <td className="px-4 py-3.5 text-right">
                    <button onClick={() => handleCancelOrder(order.id)} disabled={isLoading}
                      className="px-4 py-2 rounded-lg text-[11px] font-syne font-bold transition-all disabled:opacity-40"
                      style={{ background: 'var(--color-redBg)', border: '1px solid var(--color-redBorder)', color: 'var(--color-redLight)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.2)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--color-redBg)'}>
                      <i className="fas fa-xmark mr-1" />Cancel
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderHistory = () => {
    if (historyOrders.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-5" style={{ background: 'var(--color-bgCard)', border: '1px solid var(--color-borderMedium)' }}>
            <i className="fas fa-clock-rotate-left text-3xl" style={{ color: 'var(--color-textDim)' }} />
          </div>
          <p className="font-mono text-base font-bold" style={{ color: 'var(--color-textSecondary)' }}>No trade history yet</p>
          <p className="font-mono text-sm mt-1" style={{ color: 'var(--color-textSecondary)' }}>Your closed trades will appear here</p>
        </div>
      );
    }

    const thStyle = { color: 'var(--color-textSecondary)', fontSize: '12px', fontFamily: 'monospace', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' };
    const tdText = { color: 'var(--color-text)', fontSize: '14px', fontFamily: 'monospace', fontWeight: 700 };
    const tdMuted = { color: 'var(--color-textSecondary)', fontSize: '14px', fontFamily: 'monospace', fontWeight: 700 };

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b-2" style={{ borderColor: 'var(--color-borderMedium)' }}>
              <th className="px-4 py-3 text-left" style={thStyle}>ID</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Symbol</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Type</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Lots</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Trade Vol</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Entry</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Open Time</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Exit</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Close Time</th>
              <th className="px-4 py-3 text-left" style={thStyle}>S/L</th>
              <th className="px-4 py-3 text-left" style={thStyle}>T/P</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Swap</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Profit</th>
              <th className="px-4 py-3 text-left" style={thStyle}>ROI</th>
              <th className="px-4 py-3 text-left" style={thStyle}>Reason</th>
            </tr>
          </thead>
          <tbody>
            {[...historyOrders].filter(Boolean).map((order, idx) => {
              const qty = Number(order.qty) || 0;
              const swap = Number(order.swap) || 0;
              const entryPrice = Number(order.entryPrice) || 0;
              const exitPrice = Number(order.exitPrice) || 0;
              // ✅ Same function as footer totalHistoryPnl
              const netPnl = getHistoryNetPnl(order);
              const cat = order.cat || 'crypto';
              const ls = getLotSize(cat, order.instId || order.instrumentId);
              const tradeVolume = (qty * ls).toLocaleString();
              const ordId = order.id ? String(order.id).slice(-6) : String(idx + 1).padStart(6, '0');
              const closeReason = order.closeReason || 'WebTrader';
              const dir = getDirectionLabel(order);
              const notional = entryPrice * qty * ls;
              const roi = notional > 0 ? ((netPnl / notional) * 100) : 0;
              const reasonColor = closeReason === 'TakeProfit' ? 'teal' : closeReason === 'StopLoss' ? 'short' : closeReason === 'PartialClose' ? 'orange' : 'purple';

              return (
                <tr key={order.id ?? idx} className="border-b transition-all" style={{ borderColor: 'var(--color-borderLight)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bgHover)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td className="px-4 py-3.5" style={tdMuted}>{ordId}</td>
                  <td className="px-4 py-3.5 font-syne font-extrabold text-base" style={tdText}>{order.instId || order.instrumentId}</td>
                  <td className="px-4 py-3.5"><Badge color={dir.color} isLight={isLight}>{dir.label}</Badge></td>
                  <td className="px-4 py-3.5" style={tdText}>{qty.toFixed(2)}</td>
                  <td className="px-4 py-3.5" style={tdText}>{tradeVolume}</td>
                  <td className="px-4 py-3.5" style={tdText}>{fmtP(entryPrice, cat)}</td>
                  <td className="px-4 py-3.5 text-xs" style={tdMuted}>{fmtDT(order.openTime || order.time)}</td>
                  <td className="px-4 py-3.5" style={tdText}>{fmtP(exitPrice, cat)}</td>
                  <td className="px-4 py-3.5 text-xs" style={tdMuted}>{fmtDT(order.closeTime)}</td>
                  <td className="px-4 py-3.5" style={{ ...tdText, color: Number(order.sl) > 0 ? 'var(--color-redLight)' : 'var(--color-textDim)' }}>{fmtSL(order.sl)}</td>
                  <td className="px-4 py-3.5" style={{ ...tdText, color: Number(order.tp) > 0 ? 'var(--color-greenLight)' : 'var(--color-textDim)' }}>{fmtTP(order.tp)}</td>
                  <td className="px-4 py-3.5" style={{ ...tdText, color: swap !== 0 ? 'var(--color-orangeLight)' : 'var(--color-textDim)' }}>
                    {swap !== 0 ? (swap < 0 ? '-$' : '$') + Math.abs(swap).toFixed(2) : '$0.00'}
                  </td>
                  <td className="px-4 py-3.5 font-mono text-base font-extrabold" style={{ color: netPnl >= 0 ? 'var(--color-green)' : 'var(--color-red)' }}>
                    {netPnl >= 0 ? '+$' : '-$'}{Math.abs(netPnl).toFixed(2)}
                  </td>
                  <td className="px-4 py-3.5 font-mono text-sm font-extrabold" style={{ color: roi >= 0 ? 'var(--color-green)' : 'var(--color-red)' }}>
                    {roi >= 0 ? '+' : ''}{roi.toFixed(2)}%
                  </td>
                  <td className="px-4 py-3.5"><Badge color={reasonColor}>{closeReason}</Badge></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const totalCount = activeTab === 'history'
    ? historyOrders.length
    : activeTab === 'pending'
      ? pendingOrders.length
      : Object.keys(portfolio).length;

  const isHistoryTab = activeTab === 'history';
  const isPendingTab = activeTab === 'pending';
  const displayPnl = isHistoryTab ? totalHistoryPnl : isPendingTab ? 0 : totalLivePnl;
  const label = isHistoryTab ? 'Profit/Losses' : isPendingTab ? 'Pending Orders' : 'Unrealized P&L';

  const displayEquity     = isHistoryTab
    ? currentBalance + totalHistoryPnl + totalCredit + bonusBalance
    : isPendingTab
      ? currentBalance + totalCredit + bonusBalance
      : equity;
  const displayMarginUsed = (isHistoryTab || isPendingTab) ? 0 : totalMarginUsed;
  const displayFreeMargin = isHistoryTab
    ? currentBalance + totalHistoryPnl + totalCredit + bonusBalance
    : isPendingTab
      ? currentBalance + totalCredit + bonusBalance
      : freeMargin;
  const displayMarginLevel = (isHistoryTab || isPendingTab || totalMarginUsed === 0) ? null : marginLevel;

  const partialClosePos = showPartialClose ? portfolio[showPartialClose] : null;
  const partialCloseTotalQty = partialClosePos ? Math.abs(Number(partialClosePos.qty)) : 0;
  const partialCloseQtyNum = Number(partialCloseQty) || 0;
  const partialCloseRemaining = partialCloseTotalQty - partialCloseQtyNum;

  return (
    <div className="fixed inset-0 z-[700] flex items-center justify-center">
      <div className="absolute inset-0" style={{ background: 'var(--color-bgOverlay)' }} onClick={onClose} />
      <div className="relative rounded-2xl w-[95vw] h-[90vh] shadow-2xl flex flex-col overflow-hidden animate-pop-in" style={{ background: 'var(--color-bg)', border: '2px solid var(--color-orangeBorder)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b-2 flex-shrink-0" style={{ background: 'var(--color-bgSurface)', borderColor: 'var(--color-borderMedium)' }}>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg" style={{ background: 'var(--color-orangeBg)', border: '2px solid var(--color-orangeBorder)', color: 'var(--color-text)' }}>
              <i className="fas fa-table-list" />
            </div>
            <div>
              <h2 className="font-syne text-xl font-extrabold" style={{ color: 'var(--color-text)' }}>Trading Panel</h2>
              <p className="text-sm font-mono font-bold" style={{ color: 'var(--color-textSecondary)' }}>Full view of all trading activity</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {tabs.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-syne font-bold transition-all border-2"
                style={{
                  background: activeTab === tab.key ? 'var(--color-orangeBg)' : 'transparent',
                  borderColor: activeTab === tab.key ? 'var(--color-orange)' : 'var(--color-borderMedium)',
                  color: activeTab === tab.key ? 'var(--color-text)' : 'var(--color-textSecondary)',
                }}>
                <i className={`fas ${tab.icon} text-[12px]`} />
                {tab.label}
                <span className="text-[11px] px-2 py-0.5 rounded-full font-bold"
                  style={{
                    background: activeTab === tab.key ? 'var(--color-orangeBg)' : 'var(--color-bgCard)',
                    color: activeTab === tab.key ? 'var(--color-text)' : 'var(--color-textSecondary)',
                  }}>{tab.count}</span>
              </button>
            ))}
            <div className="w-px h-6" style={{ background: 'var(--color-borderStrong)' }} />
            <button onClick={onClose}
              className="w-10 h-10 rounded-lg cursor-pointer text-lg flex items-center justify-center transition-all"
              style={{ background: 'var(--color-bgCard)', border: '2px solid var(--color-borderStrong)', color: 'var(--color-textSecondary)' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-red)'; e.currentTarget.style.color = 'var(--color-red)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-borderStrong)'; e.currentTarget.style.color = 'var(--color-textSecondary)'; }}>
              <i className="fas fa-xmark" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto custom-scrollbar p-4">
          {activeTab === 'active' && renderActive()}
          {activeTab === 'pending' && renderPending()}
          {activeTab === 'history' && renderHistory()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t-2 flex-shrink-0 flex-wrap gap-y-2" style={{ background: 'var(--color-bgSurface)', borderColor: 'var(--color-borderMedium)' }}>
          <div className="flex items-center gap-5 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-mono font-bold uppercase tracking-wider" style={{ color: 'var(--color-textSecondary)' }}>
                {isHistoryTab ? 'Closed' : isPendingTab ? 'Pending' : 'Active'}
              </span>
              <span className="text-sm font-mono font-extrabold" style={{ color: 'var(--color-text)' }}>{totalCount}</span>
            </div>
            {activeTab !== 'pending' && (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-mono font-bold uppercase tracking-wider" style={{ color: 'var(--color-textSecondary)' }}>Equity</span>
                  <span className="text-sm font-mono font-extrabold" style={{ color: 'var(--color-text)' }}>
                    {fmtUSD(displayEquity)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-mono font-bold uppercase tracking-wider" style={{ color: 'var(--color-textSecondary)' }}>Margin Used</span>
                  <span className="text-sm font-mono font-extrabold" style={{ color: 'var(--color-yellow)' }}>
                    {fmtUSD(displayMarginUsed)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-mono font-bold uppercase tracking-wider" style={{ color: 'var(--color-textSecondary)' }}>Free Margin</span>
                  <span className="text-sm font-mono font-extrabold" style={{ color: displayFreeMargin >= 0 ? 'var(--color-greenLight)' : 'var(--color-red)' }}>
                    {fmtUSD(displayFreeMargin)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-mono font-bold uppercase tracking-wider" style={{ color: 'var(--color-textSecondary)' }}>Margin Level</span>
                  <span className="text-sm font-mono font-extrabold" style={{ color: displayMarginLevel === null ? 'var(--color-textSecondary)' : displayMarginLevel > 200 ? 'var(--color-greenLight)' : displayMarginLevel > 100 ? 'var(--color-yellow)' : 'var(--color-red)' }}>
                    {displayMarginLevel !== null ? displayMarginLevel.toFixed(1) + '%' : '—'}
                  </span>
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-4 ml-auto">
            <div className="flex items-center gap-2">
              <span className="text-[12px] font-mono font-bold uppercase tracking-wider" style={{ color: 'var(--color-textSecondary)' }}>{label}:</span>
              <span className="text-lg font-mono font-extrabold" style={{ color: displayPnl >= 0 ? 'var(--color-green)' : 'var(--color-red)' }}>
                {displayPnl !== 0 ? (displayPnl >= 0 ? '+' : '') + fmtUSD(Math.abs(displayPnl)) : fmtUSD(0)}
              </span>
            </div>
            <button onClick={onClose}
              className="px-6 py-2.5 rounded-lg text-sm font-syne font-bold transition-all"
              style={{ background: 'var(--color-bgCard)', border: '1px solid var(--color-borderHover)', color: 'var(--color-textSecondary)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-bgHover)'; e.currentTarget.style.color = 'var(--color-text)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--color-bgCard)'; e.currentTarget.style.color = 'var(--color-textSecondary)'; }}>
              <i className="fas fa-xmark mr-1.5" />Close
            </button>
          </div>
        </div>
      </div>

      {/* Partial Close Modal */}
      {showPartialClose && partialClosePos && (
        <div className="fixed inset-0 z-[800] flex items-center justify-center">
          <div className="absolute inset-0" style={{ background: 'var(--color-bgOverlay)' }} onClick={closePartialCloseModal} />
          <div className="relative rounded-2xl w-full max-w-md mx-4 shadow-2xl" style={{ background: 'var(--color-bgSurface)', border: '2px solid var(--color-orangeBorder)' }}>
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--color-borderStrong)' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)' }}>
                  <i className="fas fa-scissors" style={{ color: 'var(--color-text)' }} />
                </div>
                <div>
                  <h3 className="font-syne text-base font-extrabold" style={{ color: 'var(--color-text)' }}>PARTIAL CLOSE</h3>
                  <p className="text-[10px] font-mono" style={{ color: 'var(--color-textSecondary)' }}>{partialClosePos.instrumentId} · {partialCloseTotalQty.toFixed(2)} lots total</p>
                </div>
              </div>
              <button onClick={closePartialCloseModal} className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
                style={{ background: 'var(--color-bgCard)', border: '1px solid var(--color-borderHover)', color: 'var(--color-textSecondary)' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-red)'; e.currentTarget.style.color = 'var(--color-red)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-borderHover)'; e.currentTarget.style.color = 'var(--color-textSecondary)'; }}>
                <i className="fas fa-times text-sm" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-[10px] font-mono font-bold uppercase tracking-wider block mb-2" style={{ color: 'var(--color-textSecondary)' }}>
                  Lots to Close (1 - {(partialCloseTotalQty - 0.01).toFixed(2)})
                </label>
                <input type="number" value={partialCloseQty} onChange={e => setPartialCloseQty(e.target.value)} step="0.01" min="0.01" max={(partialCloseTotalQty - 0.01).toFixed(2)}
                  placeholder="Enter lot size"
                  className="w-full rounded-lg px-4 py-3 font-mono text-sm font-bold outline-none transition-all"
                  style={{ background: 'var(--color-bgInput)', border: '1px solid var(--color-borderMedium)', color: 'var(--color-text)' }}
                  onFocus={e => e.target.style.borderColor = 'var(--color-orange)'}
                  onBlur={e => e.target.style.borderColor = 'var(--color-borderMedium)'}
                  autoFocus />
              </div>
              {partialCloseQtyNum > 0 && partialCloseQtyNum < partialCloseTotalQty && (
                <div className="p-3 rounded-lg space-y-2" style={{ background: 'var(--color-bgCard)', border: '1px solid var(--color-borderMedium)' }}>
                  <div className="flex justify-between text-[10px]">
                    <span className="font-mono" style={{ color: 'var(--color-textSecondary)' }}>Closing</span>
                    <span className="font-mono font-bold" style={{ color: 'var(--color-redLight)' }}>{partialCloseQtyNum.toFixed(2)} lots</span>
                  </div>
                  <div className="flex justify-between text-[10px]">
                    <span className="font-mono" style={{ color: 'var(--color-textSecondary)' }}>Remaining</span>
                    <span className="font-mono font-bold" style={{ color: 'var(--color-greenLight)' }}>{partialCloseRemaining.toFixed(2)} lots</span>
                  </div>
                  <div className="border-t pt-2 text-[9px] font-mono" style={{ borderColor: 'var(--color-borderMedium)', color: 'var(--color-textSecondary)' }}>
                    Margin will be recalculated for the remaining position. P&L for closed lots will be added to balance.
                  </div>
                </div>
              )}
              <div className="flex gap-3">
                <button onClick={closePartialCloseModal}
                  className="flex-1 py-2.5 rounded-lg font-syne font-bold text-xs transition-all"
                  style={{ background: 'var(--color-bgCard)', border: '1px solid var(--color-borderHover)', color: 'var(--color-textSecondary)' }}>CANCEL</button>
                <button onClick={handlePartialClose}
                  disabled={partialClosing || !partialCloseQtyNum || partialCloseQtyNum <= 0 || partialCloseQtyNum >= partialCloseTotalQty}
                  className="flex-1 py-2.5 rounded-lg font-syne font-bold text-xs transition-all disabled:opacity-40"
                  style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)', color: 'var(--color-text)' }}
                  onMouseEnter={e => { if (!partialClosing) e.currentTarget.style.background = 'rgba(46,54,66,0.55)'; }}
                  onMouseLeave={e => { if (!partialClosing) e.currentTarget.style.background = 'var(--color-orangeBg)'; }}>
                  {partialClosing ? 'CLOSING...' : 'CONFIRM PARTIAL CLOSE'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pop-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-pop-in { animation: pop-in 0.2s ease-out; }
        .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: var(--color-scrollbarThumb); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: var(--color-scrollbarThumbHover); }
      `}</style>
    </div>
  );
};

export default FullViewPanel;